<template>
  <div class="space-y-4">
    <div class="flex flex-col items-start justify-between gap-2 md:flex-row">
      <h2>{{ t('bookingOverview') }}</h2>
      <CVButton
        v-if="booking.status !== BookingStatus.FINALIZED"
        @click="showEditBookingDatesAndLocations = true"
        >{{ t('changeBookingDatesAndLocations') }} <PencilIcon class="size-4"
      /></CVButton>
    </div>
    <div class="space-y-4">
      <template v-for="(allocation, index) in booking.allocations" :key="allocation.id">
        <div class="flex gap-4">
          <div class="flex flex-col items-center gap-3">
            <FlagIcon class="size-4 text-hazel-500 lg:size-6" />
            <TimelineArrow class="flex-1" />
          </div>
          <div class="flex min-w-0 flex-1 flex-col gap-4">
            <TimelineLocation
              :booking="booking"
              :allocation="allocation"
              :earlier-transaction-date="
                index > 0 &&
                checkForSeparateTransactions(index - 1) &&
                hasMutualCarSwap(booking.allocations[index - 1].id, allocation.id)
                  ? booking.allocations[index - 1].endDate
                  : undefined
              "
              :earlier-transaction-text="t('swappedEarlier')"
              mode="pickup"
              @edit="handleEdit(index, 'time-and-location-pickup')"
            />

            <TimelineAllocation
              :allocation="allocation"
              :booking="booking"
              @edit="handleEdit(index, 'car')"
              @delete="deleteCarSwap(index)"
              @delete-transfer="handleDeleteTransfer($event, index)"
            />
            <template
              v-if="
                index === booking.allocations.length - 1 && new Date() < new Date(booking.endDate)
              "
            >
              <CVButton
                v-if="
                  ![
                    BookingStatus.HANDED_OVER,
                    BookingStatus.CAR_RETURNED,
                    BookingStatus.CANCELED,
                    BookingStatus.FINALIZED,
                  ].includes(booking.status)
                "
                class="w-full"
                @click.prevent="handleEdit(index, 'changeBookedCarMode')"
              >
                {{ t('changeBookedCar') }}
              </CVButton>
              <CVButton
                v-if="booking.status === BookingStatus.HANDED_OVER"
                class="w-full"
                @click.prevent="addCarSwap"
              >
                + {{ t('addCarSwap') }}
              </CVButton>
            </template>
          </div>
        </div>
        <template
          v-if="
            index === booking.allocations.length - 1 ||
            (checkForSeparateTransactions(index) &&
              !hasMutualCarSwap(allocation.id, booking.allocations[index + 1].id))
          "
        >
          <div class="flex gap-4">
            <FlagIcon class="size-4 flex-shrink-0 text-hazel-500 lg:size-6" />
            <div class="w-full flex-1">
              <TimelineLocation
                :booking="booking"
                :allocation="allocation"
                mode="dropoff"
                :earlier-transaction-date="
                  index === booking.allocations.length - 1 && booking.endDate > allocation.endDate
                    ? allocation.endDate
                    : undefined
                "
                :earlier-transaction-text="t('returnedEarlier')"
                :show-booking-date="index === booking.allocations.length - 1"
                @edit="handleEdit(index, 'time-and-location-dropoff')"
              />
              <div
                v-if="
                  index < booking.allocations.length - 1 &&
                  allocation.status !== AllocationStatus.RETURNED
                "
                class="rounded-md bg-card-warning-bg px-4 py-2 text-card-warning-content"
              >
                {{ t('separateTransactionsWarning') }}
              </div>
            </div>
          </div>
          <ZigZagLine v-if="index < booking.allocations.length - 1" class="flex-1 text-hazel-600" />
        </template>
      </template>
    </div>
    <Modal
      :model-value="currentStep === 'swapCarMode'"
      @update:model-value="$event === false && handleCancel()"
    >
      <div class="space-y-4">
        <div class="text-lg font-semibold">{{ t('swapCarModeTitle') }}</div>
        <p>{{ t('swapCarModeText') }}</p>
        <div class="space-y-3">
          <CVButton class="w-full" variant="default" @click="handleSetCarSwapMode(false)">
            {{ t('noSeparateTransactions') }}
          </CVButton>
          <CVButton class="w-full" variant="secondary" @click="handleSetCarSwapMode(true)">
            {{ t('separateTransactions') }}
          </CVButton>
        </div>
      </div>
    </Modal>
    <Modal
      :model-value="
        currentStep === 'time-and-location-dropoff' || currentStep === 'time-and-location-pickup'
      "
      content-class="!overflow-y-visible"
      :close-on-click-outside="false"
    >
      <TimeAndLocationForm
        :key="currentStep ?? ''"
        :booking="booking"
        :allocation-index="
          currentStep === 'time-and-location-dropoff' && isNewCarSwap
            ? currentAllocationIndex - 1
            : currentAllocationIndex
        "
        :mode="currentStep === 'time-and-location-dropoff' ? 'dropoff' : 'pickup'"
        :is-new-car-swap="isNewCarSwap"
        :is-saving="isBusy"
        @confirm="handleConfirmTimeAndLocation"
        @cancel="handleCancel"
        @back="handleBack"
      />
    </Modal>
    <Modal
      :model-value="currentStep === 'changeBookedCarMode'"
      @update:model-value="$event === false && handleCancel()"
    >
      <div class="space-y-4">
        <div class="text-lg font-semibold">{{ t('whyChangeCarTitle') }}</div>
        <div class="space-y-3">
          <CVButton class="w-full" variant="default" @click="handleEdit(0, 'bookedCar')">
            {{ t('customerWantsDifferentCar') }}
          </CVButton>
          <CVButton class="w-full" variant="secondary" @click="handleEdit(0, 'car')">
            {{ t('internalUpgradeRebooking') }}
          </CVButton>
        </div>
      </div>
    </Modal>
    <Modal
      :model-value="currentStep === 'car' || currentStep === 'bookedCar'"
      @update:model-value="$event === false && handleCancel()"
    >
      <SelectCarForm
        :start-date="
          currentAllocationIndex === 0
            ? booking.startDate
            : (fields[currentAllocationIndex]?.value.startDate ?? '')
        "
        :end-date="booking.allocations[currentAllocationIndex + 1]?.startDate || booking.endDate"
        :transfer-delivery-location="transferDeliveryLocation"
        :transfer-return-location="transferReturnLocation"
        :booking-created-date="currentStep === 'bookedCar' ? booking.createdDate : undefined"
        :initial-station-id="getCurrentInitialStationId()"
        :show-back-button="isNewCarSwap"
        :partner-id="booking.agency?.id"
        :ignore-occupation-of-car-id="booking.allocations[currentAllocationIndex]?.car.id"
        :hide-prices="currentStep !== 'bookedCar'"
        :show-transfer-breakdown="currentStep === 'bookedCar'"
        :insurance-case-id="booking.insuranceCase?.id"
        :tax-percent="offerTaxPercent"
        :select-offer="handleSelectOffer"
        @back="handleBack"
      />
    </Modal>
    <Modal
      :model-value="currentStep === 'reason'"
      content-class="!overflow-y-visible"
      :close-on-click-outside="false"
    >
      <CarSwapReasonForm
        :booking="booking"
        :allocation-index="currentAllocationIndex"
        :is-saving="isBusy"
        @save="handleSave"
        @cancel="handleCancel"
        @back="handleBack"
      />
    </Modal>
    <Modal
      :model-value="showEditBookingDatesAndLocations"
      content-class="!overflow-y-visible"
      :show-close="true"
      @update:model-value="
        () => {
          handleCancel();
          showEditBookingDatesAndLocations = false;
        }
      "
    >
      <BookingDatesAndLocationsForm
        :booking="booking"
        :is-saving="isBusy"
        @save="handleConfirmTimeAndLocation(true)"
        @cancel="handleCancel"
      />
    </Modal>
  </div>
</template>

<script setup lang="ts">
import type { Booking, UpdateBookingDto } from '@/entities/bookings/booking.entity';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import TimelineAllocation from './TimelineAllocation.vue';
import TimelineLocation from './TimelineLocation.vue';
import CVButton from '../buttons/CVButton.vue';
import { useFieldArray, useForm } from 'vee-validate';
import {
  AllocationStatus,
  AllocationType,
  type BookingAllocationDto,
  type Offer,
  PriceRecalculationMode,
} from '@carvia/ros-client-types';
import { pointToLocationDto } from '@/hooks/use-location-to-coordinates';
import TimeAndLocationForm from './TimeAndLocationForm.vue';
import { BookingLocationType } from '@/entities/bookings/booking-location-type.enum';
import Modal from '../Modal.vue';
import * as yup from 'yup';
import TimelineArrow from './TimelineArrow.vue';
import { FlagIcon, PencilIcon } from 'lucide-vue-next';
import SelectCarForm from '@/views/handover/components/overview/SelectCarForm.vue';
import CarSwapReasonForm from './CarSwapReasonForm.vue';
import { getStationOfCarByDate } from '@/hooks/use-current-station-of-car';
import { getNewCarSwapDate } from './utils/get-new-car-swap-date';
import { getOutOfRangeAllocationIndices } from './utils/filter-out-of-range-allocations';
import { isEqual } from 'lodash';
import ZigZagLine from '../ZigZagLine.vue';
import { BookingStatus } from '@/entities/bookings/booking-status.enum';
import { Alert } from '@/services/alert.service';
import type { Allocation } from '@/entities/allocations/allocation.entity.ts';
import { bookingLocationToCoordinates } from '@/utils/booking-location-to-coordinates.ts';
import BookingDatesAndLocationsForm from './BookingDatesAndLocationsForm.vue';
import { usePriceChangeConfirmation } from '@/components/price-change-confirmation/price-change-confirmation';
import { isSameDateTime } from '@/utils/is-same-date-time';
import { resolvePricingTaxPercent } from '@/utils/tax-procedure';

type Step =
  | 'swapCarMode'
  | 'time-and-location-dropoff'
  | 'time-and-location-pickup'
  | 'car'
  | 'reason'
  | 'bookedCar'
  | 'changeBookedCarMode'
  | null;

const props = defineProps<{
  booking: Booking;
}>();

const { t } = useI18n();

const offerTaxPercent = computed(() =>
  resolvePricingTaxPercent(props.booking.priceCalculation?.taxPercent, props.booking.taxProcedure),
);

const currentStep = ref<Step>(null);
const currentAllocationIndex = ref(0);
const isNewCarSwap = ref(false);
const showEditBookingDatesAndLocations = ref(false);
const { confirmAndSave, isBusy, reset } = usePriceChangeConfirmation();

const transferDeliveryLocation = computed(() =>
  bookingLocationToCoordinates(props.booking, 'pickup'),
);
const transferReturnLocation = computed(() =>
  bookingLocationToCoordinates(props.booking, 'dropoff'),
);

const initBookingDto = (booking: Booking) => {
  return {
    bookedCarId: booking.bookedCar.id,
    startDate: new Date(booking.startDate),
    endDate: new Date(booking.endDate),
    pickupLocationType: booking.pickupLocationType,
    pickupLocation: pointToLocationDto(booking.pickupLocation),
    pickupLocationGeocodedAddress: booking.pickupLocationGeocodedAddress,
    pickupStationId: booking.pickupStation?.id ?? null,
    dropoffLocationType: booking.dropoffLocationType,
    dropoffLocation: pointToLocationDto(booking.dropoffLocation),
    dropoffLocationGeocodedAddress: booking.dropoffLocationGeocodedAddress,
    dropoffStationId: booking.dropoffStation?.id ?? null,
    allocations: booking.allocations.map((a, index) => {
      const hasPickup = index > 0;
      const hasDropoff =
        index < booking.allocations.length - 1 && checkForSeparateTransactions(index);
      return {
        id: a.id,
        carId: a.car.id,
        startDate: hasPickup ? new Date(a.startDate) : null,
        endDate: hasDropoff ? new Date(a.endDate) : null,
        pickupLocationType: hasPickup ? a.pickupLocationType : null,
        pickupLocation: hasPickup ? pointToLocationDto(a.pickupLocation) : null,
        pickupLocationGeocodedAddress: hasPickup ? a.pickupLocationGeocodedAddress : null,
        pickupResponsibleStationId: hasPickup ? (a.pickupResponsibleStation?.id ?? null) : null,
        dropoffLocationType: hasDropoff ? a.dropoffLocationType : null,
        dropoffLocation: hasDropoff ? pointToLocationDto(a.dropoffLocation) : null,
        dropoffLocationGeocodedAddress: hasDropoff ? a.dropoffLocationGeocodedAddress : null,
        dropoffResponsibleStationId: hasDropoff ? (a.dropoffResponsibleStation?.id ?? null) : null,
      };
    }),
  };
};

const {
  handleSubmit,
  values: formValues,
  setValues,
  setFieldValue,
  resetForm,
} = useForm<UpdateBookingDto>({
  initialValues: initBookingDto(props.booking),
  validationSchema: yup.object({
    allocations: yup
      .array()
      .test(
        'dates-are-in-order',
        'Dates must be in between surrounding allocations',
        (allocations) => {
          return allocations?.every((a, index) => {
            if (index === 0) return true;
            return (
              new Date(a.startDate).getTime() >
              new Date(allocations[index - 1]?.startDate ?? props.booking.startDate).getTime()
            );
          });
        },
      ),
  }),
  keepValuesOnUnmount: true,
});

const { fields, push, remove, update } =
  useFieldArray<Partial<BookingAllocationDto>>('allocations');

const addCarSwap = () => {
  push({
    id: undefined,
    carId: undefined,
    startDate: getNewCarSwapDate(props.booking.allocations),
    pickupLocationType: BookingLocationType.STATION,
    pickupLocation: null,
    pickupLocationGeocodedAddress: null,
    pickupResponsibleStationId: null,
  });
  update(fields.value.length - 2, {
    ...fields.value[fields.value.length - 2].value,
    endDate: null,
    dropoffLocationType: null,
    dropoffLocation: null,
    dropoffLocationGeocodedAddress: null,
    dropoffResponsibleStationId: null,
  });
  currentAllocationIndex.value = fields.value.length - 1;
  isNewCarSwap.value = true;
  currentStep.value = 'swapCarMode';
};

const handleSetCarSwapMode = (hasSeparateTransactions: boolean) => {
  if (hasSeparateTransactions) {
    const previousAllocation = props.booking.allocations[currentAllocationIndex.value - 1];
    update(currentAllocationIndex.value - 1, {
      ...fields.value[currentAllocationIndex.value - 1].value,
      endDate: new Date(previousAllocation.endDate),
      dropoffLocationType: BookingLocationType.STATION,
      dropoffLocation: null,
      dropoffLocationGeocodedAddress: null,
      dropoffResponsibleStationId: getStationOfCarByDate(
        previousAllocation.car,
        previousAllocation.startDate,
      )?.id,
    });
  }
  currentStep.value = 'time-and-location-pickup';
};

const handleEdit = (index: number, step: Step) => {
  currentAllocationIndex.value = index;
  isNewCarSwap.value = false;
  currentStep.value = step;
};

const deleteCarSwap = async (index: number) => {
  remove(index);
  update(0, {
    ...fields.value[0].value,
    startDate: null,
    pickupLocationType: null,
    pickupLocation: null,
    pickupLocationGeocodedAddress: null,
    pickupResponsibleStationId: null,
  });
  currentStep.value = null;
  await handleSave();
};

const handleBack = () => {
  if (currentStep.value === 'reason') {
    currentStep.value = 'car';
  } else if (currentStep.value === 'car') {
    currentStep.value = 'time-and-location-pickup';
    return;
  } else if (currentStep.value === 'time-and-location-pickup') {
    currentStep.value = 'time-and-location-dropoff';
  }
};

const isPricingRelevantLocationChange = (isFinalStep: boolean) =>
  isFinalStep ||
  (currentAllocationIndex.value === 0 && currentStep.value === 'time-and-location-pickup') ||
  (currentAllocationIndex.value === props.booking.allocations.length - 1 &&
    currentStep.value === 'time-and-location-dropoff');

const handleConfirmTimeAndLocation = async (isFinalStep = false) => {
  if (!isNewCarSwap.value || isFinalStep) {
    if (isPricingRelevantLocationChange(isFinalStep)) {
      await saveWithPriceConfirmation();
    } else {
      await handleSave();
    }
    return;
  }
  currentStep.value = 'car';
};

const handleSelectOffer = async (offer: Offer) => {
  if (currentStep.value === 'bookedCar') {
    const { isConfirmed } = await Alert.fire({
      text: t('reallyChangeBookedCar'),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonText: t('changeBookedCar'),
      denyButtonText: t('cancel'),
    });

    if (!isConfirmed) {
      return;
    }

    setFieldValue('bookedCarId', offer.car.id);
    update(currentAllocationIndex.value, {
      ...fields.value[currentAllocationIndex.value].value,
      carId: offer.car.id,
    });
    await saveWithPriceConfirmation({ resetBookedExtras: true });
    return;
  }

  update(currentAllocationIndex.value, {
    ...fields.value[currentAllocationIndex.value].value,
    carId: offer.car.id,
  });

  if (isNewCarSwap.value) {
    currentStep.value = 'reason';
  } else {
    await handleSave();
  }
};

const handleDeleteTransfer = async (transfer: Allocation, allocationIndex: number) => {
  const transferOf = props.booking.allocations[allocationIndex];
  let mode = transfer.type === AllocationType.TRANSFER_DELIVERY ? 'pickup' : 'dropoff';
  const carStation = getStationOfCarByDate(transferOf.car, transferOf.startDate);
  const hasSeparateTransactions = checkForSeparateTransactions(allocationIndex);
  const isLastAllocation = allocationIndex === props.booking.allocations.length - 1;
  let index = allocationIndex;
  if (mode === 'dropoff' && !hasSeparateTransactions && !isLastAllocation) {
    index = allocationIndex + 1;
    mode = 'pickup';
  }
  const isBookingLocations =
    (mode === 'pickup' && index === 0) || (mode === 'dropoff' && isLastAllocation);
  if (isBookingLocations) {
    setValues({
      ...formValues,
      [`${mode}LocationType`]: BookingLocationType.STATION,
      [`${mode}StationId`]: carStation.id,
      [`${mode}Location`]: null,
      [`${mode}LocationGeocodedAddress`]: null,
    });
    await saveWithPriceConfirmation();
  } else {
    if (
      (
        await Alert.fire({
          titleText: t('reallyDeleteTransferTitle'),
          text: t('reallyDeleteTransferText'),
          icon: 'warning',
          showDenyButton: true,
          showCancelButton: true,
          showConfirmButton: false,
          denyButtonText: t('delete'),
          cancelButtonText: t('keep'),
        })
      ).isDismissed
    ) {
      return;
    }
    update(index, {
      ...fields.value[index].value,
      [`${mode}LocationType`]:
        hasSeparateTransactions || mode === 'pickup' ? BookingLocationType.STATION : null,
      [`${mode}ResponsibleStationId`]:
        hasSeparateTransactions || mode === 'pickup' ? carStation.id : null,
      [`${mode}Location`]: null,
      [`${mode}LocationGeocodedAddress`]: null,
    });
    await handleSave();
  }
};

function buildPricingUpdateDto(options: { resetBookedExtras?: boolean } = {}): UpdateBookingDto {
  const timeChanged =
    !isSameDateTime(formValues.startDate, props.booking.startDate) ||
    !isSameDateTime(formValues.endDate, props.booking.endDate);
  const recalculatingBookedCar = formValues.bookedCarId !== props.booking.bookedCar.id;

  return {
    ...formValues,
    pricing: {
      priceRecalculationMode:
        recalculatingBookedCar || options.resetBookedExtras
          ? PriceRecalculationMode.RECALCULATE
          : timeChanged
            ? props.booking.insuranceCase
              ? PriceRecalculationMode.SCALE
              : PriceRecalculationMode.REBOOK
            : PriceRecalculationMode.REBOOK,
      bookedExtras: options.resetBookedExtras ? null : undefined,
    },
  };
}

function buildStructuralUpdateDto(): UpdateBookingDto {
  return {
    ...formValues,
    // Car swaps and allocation-structure edits keep the existing rental price.
    pricing: {
      priceRecalculationMode: PriceRecalculationMode.USE_EXISTING,
    },
  };
}

const ensureAllocationsInRange = async (): Promise<boolean> => {
  if (!formValues.endDate) {
    return true;
  }

  const allocationsToDelete = getOutOfRangeAllocationIndices(
    formValues.allocations,
    formValues.endDate,
    formValues.startDate,
  );

  for (const index of allocationsToDelete) {
    if (
      [AllocationStatus.HANDED_OVER, AllocationStatus.RETURNED].includes(
        props.booking.allocations[index]?.status,
      )
    ) {
      await Alert.fire({
        text: t('cantDeleteHandedOverCarSwap'),
        icon: 'error',
      });
      return false;
    }
  }
  if (allocationsToDelete.length === 0) {
    return true;
  }
  const { isConfirmed } = await Alert.fire({
    title: t('deleteAllocationsOutsideOfBookingTimeRangeTitle'),
    text: t('deleteAllocationsOutsideOfBookingTimeRangeText', {
      count: allocationsToDelete.length,
    }),
    icon: 'warning',
    buttons: [
      {
        text: t('deleteAndContinue'),
        propertyName: 'isConfirmed',
        style: 'danger',
      },
      {
        text: t('cancel'),
        propertyName: 'isDenied',
        style: 'ghost',
      },
    ],
  });
  if (!isConfirmed) {
    return false;
  }
  allocationsToDelete.reverse().forEach((index) => {
    remove(index);
  });
  return true;
};

const finishSuccessfulSave = (savedBooking: Booking) => {
  resetForm({
    values: initBookingDto(savedBooking),
  });
  currentStep.value = null;
  showEditBookingDatesAndLocations.value = false;
};

const revertToSavedBooking = () => {
  resetForm({ values: initBookingDto(props.booking) });
};

const saveWithPriceConfirmation = async (options: { resetBookedExtras?: boolean } = {}) => {
  if (!(await ensureAllocationsInRange())) {
    return;
  }

  const { status, savedBooking } = await confirmAndSave({
    booking: props.booking,
    updateBookingDto: buildPricingUpdateDto(options),
    allowKeepOldPrice: true,
  });

  if (status === 'saved' && savedBooking) {
    finishSuccessfulSave(savedBooking);
  }
};

const getCurrentInitialStationId = () => {
  return (
    fields.value[currentAllocationIndex.value]?.value.pickupResponsibleStationId ??
    props.booking.allocations[0].pickupResponsibleStation?.id ??
    getStationOfCarByDate(
      props.booking.allocations[0]?.car,
      props.booking.allocations[0]?.startDate,
    ).id ??
    null
  );
};

const handleSave = handleSubmit(async () => {
  if (!(await ensureAllocationsInRange())) {
    return;
  }

  const { status, savedBooking } = await confirmAndSave({
    booking: props.booking,
    updateBookingDto: buildStructuralUpdateDto(),
    allowKeepOldPrice: true,
  });

  if (status === 'saved' && savedBooking) {
    finishSuccessfulSave(savedBooking);
  } else if (!currentStep.value) {
    // e.g. deleteCarSwap already mutated the form; restore if confirm was cancelled/failed
    revertToSavedBooking();
  }
});

const handleCancel = () => {
  revertToSavedBooking();
  currentStep.value = null;
  showEditBookingDatesAndLocations.value = false;
  reset();
};

function checkForSeparateTransactions(allocationIndex: number) {
  if (allocationIndex === props.booking.allocations.length - 1) {
    return false;
  }
  const allocation = props.booking.allocations[allocationIndex];
  const nextAllocation = props.booking.allocations[allocationIndex + 1];
  return (
    nextAllocation.pickupLocationType !== allocation.dropoffLocationType ||
    (nextAllocation.pickupLocationType === BookingLocationType.STATION &&
      nextAllocation.pickupResponsibleStation?.id !== allocation.dropoffResponsibleStation?.id) ||
    (nextAllocation.pickupLocationType === BookingLocationType.STREET &&
      !isEqual(nextAllocation.pickupLocation, allocation.dropoffLocation)) ||
    nextAllocation.startDate !== allocation.endDate
  );
}

function hasMutualCarSwap(swapFromId: string, swapToId: string) {
  return props.booking.carSwaps.some(
    (swap) => swap.handedOverAllocationId === swapToId && swap.returnedAllocationId === swapFromId,
  );
}
</script>

<i18n lang="json">
{
  "en": {
    "addCarSwap": "Add car swap",
    "bookingOverview": "Booking Overview",
    "downloadProtocol": "Download protocol",
    "changeBookedCar": "Change booked car",
    "reallyChangeBookedCar": "This will reset all extras and change the price.",
    "reallyDeleteTransferTitle": "Delete Transfer",
    "reallyDeleteTransferText": "Are you sure you want to delete this transfer?",
    "delete": "Delete",
    "keep": "Keep",
    "whyChangeCarTitle": "Why are you changing the car?",
    "customerWantsDifferentCar": "Customer wants a different car (Price will change)",
    "internalUpgradeRebooking": "Internal upgrade / rebooking (no price change)",
    "swapCarModeTitle": "Return swapped car at handover?",
    "swapCarModeText": "Is the car that is to be swapped still with the customer and is a return with transfer to the station possible?",
    "noSeparateTransactions": "Yes, I take/drive the car back",
    "separateTransactions": "No, the car stays at the place or is transported with a towing vehicle",
    "separateTransactionsWarning": "Attention! The car has not been returned yet, as it was not possible for the Rental Agent to take it back/bring it to our station with the exchange of the replacement car!",
    "swappedEarlier": "Swapped earlier",
    "returnedEarlier": "Returned earlier",
    "deleteAllocationsOutsideOfBookingTimeRangeTitle": "Car swaps will be deleted",
    "deleteAllocationsOutsideOfBookingTimeRangeText": "When performing this action, one car swap outside of the booking time range will be deleted. Do you want to continue?|When performing this action, {count} car swaps outside of the booking time range will be deleted. Do you want to continue?",
    "changeBookingDatesAndLocations": "Change booking dates and locations",
    "deleteAndContinue": "Delete and continue",
    "cantDeleteHandedOverCarSwap": "A car swap cannot be deleted, as it has already been handed over."
  },
  "de": {
    "addCarSwap": "Autotausch hinzufügen",
    "bookingOverview": "Buchungsübersicht",
    "downloadProtocol": "Protokoll herunterladen",
    "changeBookedCar": "Gebuchtes Fahrzeug ändern",
    "reallyChangeBookedCar": "Dies wird alle Extras zurücksetzen und den Preis ändern.",
    "reallyDeleteTransferTitle": "Transfer löschen",
    "reallyDeleteTransferText": "Sind Sie sicher, dass Sie diesen Transfer löschen möchten?",
    "delete": "Löschen",
    "keep": "Behalten",
    "whyChangeCarTitle": "Warum wird das Fahrzeug geändert?",
    "customerWantsDifferentCar": "Kunde wünscht anderes Fahrzeug (Preis wird geändert)",
    "internalUpgradeRebooking": "Internes Upgrade / Umbuchung (keine Preisänderung)",
    "swapCarModeTitle": "Ausgetauschtes Fahrzeug bei Übergabe zurücknehmen?",
    "swapCarModeText": "Steht das Fahrzeug, das getauscht werden soll, noch beim Mieter und ist eine Rücknahme mit Überführung zur Station möglich?",
    "noSeparateTransactions": "Ja, Ich nehme/fahre das Fahrzeug wieder zurück",
    "separateTransactions": "Nein, das Auto bleibt am Ort oder wurde/wird mit einem Abschleppfahrzeug weitertransportiert",
    "separateTransactionsWarning": "Achtung! Auto wurde noch nicht zurückgenommen, da es dem Rental Agent mit Tausch des Ersatzwagens nicht möglich war, das Auto zurückzunehmen/an unsere Station zu bringen!",
    "swappedEarlier": "Früher getauscht",
    "returnedEarlier": "Früher zurückgegeben",
    "deleteAllocationsOutsideOfBookingTimeRangeTitle": "Autotausche werden gelöscht",
    "deleteAllocationsOutsideOfBookingTimeRangeText": "Bei dieser Aktion wird ein Autotausch außerhalb des Buchungszeitraums gelöscht. Möchtest du fortfahren?|Bei dieser Aktion werden {count} Autotausche außerhalb des Buchungszeitraums gelöscht. Möchtest du fortfahren?",
    "changeBookingDatesAndLocations": "Buchungszeiten/-orte ändern",
    "deleteAndContinue": "Löschen und fortfahren",
    "cantDeleteHandedOverCarSwap": "Die Aktion kann nicht durchgeführt werden, da ein bereits übergebener Autotausch gelöscht werden würde."
  },
  "es": {
    "addCarSwap": "Añadir cambio de vehículo",
    "bookingOverview": "Resumen de la reserva",
    "downloadProtocol": "Descargar protocolo",
    "changeBookedCar": "Cambiar vehículo reservado",
    "reallyChangeBookedCar": "Se restablecerán todos los extras y cambiará el precio.",
    "reallyDeleteTransferTitle": "Eliminar traslado",
    "reallyDeleteTransferText": "¿Seguro que desea eliminar este traslado?",
    "delete": "Eliminar",
    "keep": "Conservar",
    "whyChangeCarTitle": "¿Por qué cambia el vehículo?",
    "customerWantsDifferentCar": "El cliente quiere otro vehículo (cambiará el precio)",
    "internalUpgradeRebooking": "Upgrade interno / nueva reserva (sin cambio de precio)",
    "swapCarModeTitle": "¿Recoger el vehículo sustituido en la entrega?",
    "swapCarModeText": "¿El vehículo a sustituir sigue con el cliente y es posible devolverlo con traslado hasta la estación?",
    "noSeparateTransactions": "Sí, lo recojo / lo conduzco de vuelta",
    "separateTransactions": "No, el coche permanece en el lugar o se transporta con grúa",
    "separateTransactionsWarning": "¡Atención! El vehículo aún no se ha devuelto, porque al agente no le fue posible recuperarlo o traerlo a nuestra estación al entregar el vehículo de sustitución.",
    "swappedEarlier": "Cambiado antes",
    "returnedEarlier": "Devuelto antes",
    "deleteAllocationsOutsideOfBookingTimeRangeTitle": "Se eliminarán cambios de vehículo",
    "deleteAllocationsOutsideOfBookingTimeRangeText": "Al hacerlo se eliminará un cambio de vehículo fuera del periodo de la reserva. ¿Desea continuar?|Al hacerlo se eliminarán {count} cambios de vehículo fuera del periodo de la reserva. ¿Desea continuar?",
    "changeBookingDatesAndLocations": "Cambiar fechas y ubicaciones de la reserva",
    "deleteAndContinue": "Eliminar y continuar",
    "cantDeleteHandedOverCarSwap": "No se puede eliminar un cambio de vehículo porque ya se ha entregado."
  }
}
</i18n>
